import React, { useState, useEffect } from 'react';
import { creditAwareFetch } from '../hooks/creditAwareFetch';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const ROLE_LABELS = { client: 'Workspace Admin', team_member: 'Workspace Member', super_admin: 'Super Admin' };
const ROLE_COLOURS = { client: '#2B6CB0', team_member: '#276749', super_admin: '#8B1A1A' };
const ROLE_BG = { client: '#EBF8FF', team_member: '#F0FFF4', super_admin: '#FFF5F5' };

export default function UserManagement({ clientId, clientName, heading = 'Users', subheading }) {
  const [users, setUsers] = useState([]);
  const [showUserForm, setShowUserForm] = useState(false);
  const [userForm, setUserForm] = useState({ email: '', name: '', role: 'client' });
  const [creatingUser, setCreatingUser] = useState(false);
  const [revokingUser, setRevokingUser] = useState(null);
  const [deactivatingUser, setDeactivatingUser] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [userError, setUserError] = useState(null);
  const [userSuccess, setUserSuccess] = useState(null);

  useEffect(() => { fetchUsers(); /* eslint-disable-next-line */ }, [clientId]);

  async function fetchUsers() {
    try {
      const res = await fetch(`${API_BASE}/clients/${clientId}/users`);
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {}
  }

  async function handleCreateUser() {
    if (!userForm.email.trim()) return;
    setCreatingUser(true);
    setUserError(null);
    setUserSuccess(null);
    try {
      const res = await creditAwareFetch(`${API_BASE}/users/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...userForm, client_id: clientId })
      });
      if (!res) return; // 402 limit_reached — modal handled by creditAwareFetch
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setUserSuccess(`Login email sent to ${userForm.email}`);
      setUserForm({ email: '', name: '', role: 'client' });
      setShowUserForm(false);
      await fetchUsers();
    } catch (e) { setUserError(e.message); }
    setCreatingUser(false);
  }

  async function handleRevokeUser(clerkUserId) {
    setRevokingUser(clerkUserId);
    try {
      await fetch(`${API_BASE}/users/${clerkUserId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      await fetchUsers();
    } catch (e) {}
    setRevokingUser(null);
    setConfirmRemove(null);
  }

  async function handleDeactivate(clerkUserId) {
    setDeactivatingUser(clerkUserId);
    try {
      await fetch(`${API_BASE}/users/${clerkUserId}/deactivate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      await fetchUsers();
    } catch (e) {}
    setDeactivatingUser(null);
  }

  async function handleReactivate(clerkUserId) {
    setDeactivatingUser(clerkUserId);
    try {
      await fetch(`${API_BASE}/users/${clerkUserId}/reactivate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      await fetchUsers();
    } catch (e) {}
    setDeactivatingUser(null);
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: '500', color: '#1A1A1A' }}>{heading}</h2>
          <p style={{ margin: 0, fontSize: '0.82rem', color: '#888' }}>{subheading || (clientName ? `Give ${clientName} team members access to their workspace in Client Engine` : 'Manage workspace user access')}</p>
        </div>
        <button onClick={() => setShowUserForm(!showUserForm)}
          style={{ backgroundColor: '#8B1A1A', color: 'white', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer', fontWeight: '500' }}>
          + Add user
        </button>
      </div>

      {userSuccess && <p style={{ color: '#276749', fontSize: '0.85rem', backgroundColor: '#F0FFF4', padding: '0.6rem 1rem', borderRadius: '6px', marginBottom: '1rem' }}>{userSuccess}</p>}
      {userError && <p style={{ color: '#C53030', fontSize: '0.85rem', backgroundColor: '#FFF5F5', padding: '0.6rem 1rem', borderRadius: '6px', marginBottom: '1rem' }}>{userError}</p>}

      {showUserForm && (
        <div style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderLeft: '3px solid #8B1A1A', borderRadius: '10px', padding: '1.25rem', marginBottom: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#4A5568', marginBottom: '0.3rem', fontWeight: '500' }}>Email *</label>
              <input value={userForm.email} onChange={e => setUserForm({...userForm, email: e.target.value})} placeholder="name@company.com"
                style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.85rem', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#4A5568', marginBottom: '0.3rem', fontWeight: '500' }}>Name</label>
              <input value={userForm.name} onChange={e => setUserForm({...userForm, name: e.target.value})} placeholder="Full name"
                style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.85rem', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#4A5568', marginBottom: '0.3rem', fontWeight: '500' }}>Role</label>
              <select value={userForm.role} onChange={e => setUserForm({...userForm, role: e.target.value})}
                style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.85rem', boxSizing: 'border-box' }}>
                <option value="client">Workspace Admin</option>
                <option value="team_member">Workspace Member</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button onClick={handleCreateUser} disabled={creatingUser || !userForm.email.trim()}
              style={{ backgroundColor: '#8B1A1A', color: 'white', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer', opacity: creatingUser || !userForm.email.trim() ? 0.5 : 1 }}>
              {creatingUser ? 'Creating & sending email...' : 'Create user & send invite'}
            </button>
            <button onClick={() => setShowUserForm(false)}
              style={{ backgroundColor: 'transparent', color: '#4A5568', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {users.length === 0 ? (
        <div style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '2rem', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#AAA' }}>No users yet. Add one above to give them access to this workspace.</p>
        </div>
      ) : (
        <div style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', overflow: 'hidden' }}>
          {users.map((u, i) => (
            <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.85rem 1.25rem', borderBottom: i < users.length - 1 ? '0.5px solid #F0EDE9' : 'none' }}>
              <div>
                <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: '500', color: '#1A1A1A' }}>{u.name || u.email}</p>
                <p style={{ margin: '0.1rem 0 0', fontSize: '0.75rem', color: '#888' }}>{u.email} · Added {new Date(u.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '500', color: ROLE_COLOURS[u.role], backgroundColor: ROLE_BG[u.role], padding: '0.2rem 0.6rem', borderRadius: '20px' }}>
                  {ROLE_LABELS[u.role] || u.role}
                </span>
                {u.deactivated_at && (
                  <span style={{ fontSize: '0.72rem', fontWeight: '500', color: '#888', backgroundColor: '#F0EDE9', padding: '0.2rem 0.6rem', borderRadius: '20px' }}>
                    Deactivated
                  </span>
                )}
                {u.deactivated_at ? (
                  <button onClick={() => handleReactivate(u.clerk_user_id)} disabled={deactivatingUser === u.clerk_user_id}
                    style={{ fontSize: '0.75rem', color: '#276749', background: 'none', border: '1px solid #9AE6B4', borderRadius: '6px', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>
                    {deactivatingUser === u.clerk_user_id ? 'Working...' : 'Reactivate'}
                  </button>
                ) : (
                  <button onClick={() => handleDeactivate(u.clerk_user_id)} disabled={deactivatingUser === u.clerk_user_id}
                    style={{ fontSize: '0.75rem', color: '#4A5568', background: 'none', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>
                    {deactivatingUser === u.clerk_user_id ? 'Working...' : 'Deactivate'}
                  </button>
                )}
                <button onClick={() => setConfirmRemove(u)} disabled={revokingUser === u.clerk_user_id}
                  style={{ fontSize: '0.75rem', color: '#C53030', background: 'none', border: '1px solid #FEB2B2', borderRadius: '6px', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmRemove && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300 }} onClick={() => setConfirmRemove(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '10px', padding: '1.5rem', maxWidth: '440px', width: '90%', boxShadow: '0 10px 30px rgba(0,0,0,0.15)' }}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: '600', color: '#1A1A1A' }}>Remove user?</h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#4A5568', lineHeight: '1.5' }}>
              This will permanently delete <strong>{confirmRemove.email}</strong> from Clerk and from this workspace. Audit trail entries created by this user will remain. This cannot be undone.
            </p>
            <p style={{ margin: '0 0 1.25rem', fontSize: '0.82rem', color: '#888' }}>
              If you only want to block access temporarily, use Deactivate instead.
            </p>
            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmRemove(null)}
                style={{ background: 'none', border: '1px solid #D4CFC9', color: '#4A5568', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={() => handleRevokeUser(confirmRemove.clerk_user_id)} disabled={revokingUser === confirmRemove.clerk_user_id}
                style={{ background: '#C53030', border: 'none', color: '#fff', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer', fontWeight: '500' }}>
                {revokingUser === confirmRemove.clerk_user_id ? 'Removing...' : 'Remove permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
